import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, CircularProgress, IconButton, Modal, Pagination, Paper, TextField, Typography} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import {useAlert} from "../context/AlertContext";
import AppTableHeaderCell from "./AppTableHeaderCell";
import {assignUserToStream, getStreamAvailableUsers} from "../api/api";
import {User} from "../types";

interface Props {
    open: boolean;
    onClose: () => void;
    streamId: number;
    onAssigned: () => void;
}

const AssignUserToStreamModal = ({open, onClose, streamId, onAssigned}: Props) => {
    const {t} = useTranslation();
    const {showAlert} = useAlert();

    const [users, setUsers] = useState<User[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [searchEmail, setSearchEmail] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const [loading, setLoading] = useState(false);
    const [assigningUserId, setAssigningUserId] = useState<number | null>(null);

    const pageSize = 10;

    const fetchAvailableUsers = async (uiPage: number, email: string) => {
        if (!streamId) return;

        try {
            setLoading(true);
            const res = await getStreamAvailableUsers(streamId, uiPage - 1, pageSize, email);
            setUsers(res.users);
            setTotalPages(res.totalPages);
            setPage(uiPage);
        } catch (e: any) {
            showAlert(e?.message ?? t("assignusertostreammodal.errors.loadAvailableUsers"), "error");
            setUsers([]);
            setTotalPages(1);
            setPage(1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;

        setUsers([]);
        setPage(1);
        setTotalPages(1);
        setSearchEmail("");
        setSearchInput("");

        fetchAvailableUsers(1, "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, streamId]);

    const onSearch = () => {
        const email = searchInput.trim();
        setSearchEmail(email);
        fetchAvailableUsers(1, email);
    };

    const onAssign = async (userId: number) => {
        try {
            setAssigningUserId(userId);
            await assignUserToStream(streamId, userId);

            showAlert(t("assignusertostreammodal.alerts.assigned"), "success");
            onAssigned();

            fetchAvailableUsers(1, searchEmail);
        } catch (e: any) {
            showAlert(e?.message ?? t("assignusertostreammodal.errors.assignFailed"), "error");
        } finally {
            setAssigningUserId(null);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "min(1000px, 100vw)",
                    maxHeight: "100vh",
                    overflow: "auto",
                    outline: "none",
                    p: 2,
                }}
            >
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        overflow: "hidden",
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            px: 2,
                            py: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                        })}
                    >
                        <Typography sx={{fontWeight: 800}} noWrap>
                            {t("assignusertostreammodal.assignUser")}
                        </Typography>

                        <Box sx={{flexGrow: 1}}/>

                        <IconButton
                            onClick={onClose}
                            aria-label={t("common.close")}
                            sx={(theme) => ({
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.divider}`,
                            })}
                        >
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Box>

                    <Box
                        sx={(theme) => ({
                            px: 2,
                            py: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                        })}
                    >
                        <TextField
                            type="search"
                            size="small"
                            placeholder={t("assignusertostreammodal.searchByEmail")}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") onSearch();
                            }}
                            sx={{width: 320}}
                        />

                        <IconButton
                            onClick={onSearch}
                            disabled={loading}
                            aria-label={t("common.search")}
                            sx={(theme) => ({
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.divider}`,
                            })}
                        >
                            <SearchIcon fontSize="small"/>
                        </IconButton>

                        <Box sx={{flexGrow: 1}}/>

                        <Typography variant="body2" color="text.secondary">
                            {t("common.pageOf", {page, totalPages})}
                        </Typography>
                    </Box>

                    {loading ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                            <CircularProgress/>
                        </Box>
                    ) : users.length === 0 ? (
                        <Box sx={{py: 6, textAlign: "center"}}>
                            <Typography variant="body1">
                                {t("assignusertostreammodal.noUsersFound")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {t("assignusertostreammodal.noUsersDescription")}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{width: "100%", overflowX: "auto"}}>
                            <table style={{width: "100%", borderCollapse: "collapse"}}>
                                <thead>
                                <tr>
                                    <AppTableHeaderCell>
                                        {t("assignusertostreammodal.fullName")}
                                    </AppTableHeaderCell>

                                    <AppTableHeaderCell>
                                        {t("assignusertostreammodal.email")}
                                    </AppTableHeaderCell>

                                    <AppTableHeaderCell align="right">
                                        {t("common.actions")}
                                    </AppTableHeaderCell>
                                </tr>
                                </thead>

                                <tbody>
                                {users.map((u) => {
                                    const assigning = assigningUserId === u.id;

                                    return (
                                        <tr key={u.id}>
                                            <td style={{padding: "12px 16px"}}>
                                                <Typography variant="body2">{u.fullName || "—"}</Typography>
                                            </td>

                                            <td style={{padding: "12px 16px"}}>
                                                <Typography variant="body2">{u.email}</Typography>
                                            </td>

                                            <td style={{padding: "12px 16px", textAlign: "right"}}>
                                                <IconButton
                                                    onClick={() => onAssign(u.id)}
                                                    disabled={assigning}
                                                    aria-label={t("assignusertostreammodal.assign")}
                                                    size="small"
                                                >
                                                    {assigning ? (
                                                        <CircularProgress size={18}/>
                                                    ) : (
                                                        <AddCircleIcon color="success"/>
                                                    )}
                                                </IconButton>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </Box>
                    )}

                    <Box sx={{display: "flex", justifyContent: "center", mt: 3, pb: 2}}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, value) => fetchAvailableUsers(value, searchEmail)}
                            color="primary"
                            disabled={loading}
                        />
                    </Box>
                </Paper>
            </Box>
        </Modal>
    );
};

export default AssignUserToStreamModal;